import { ENV } from '../environments/environment';
import SecureStorageService from './SecureStorageService';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
}

interface RefreshTokenResponse {
  is_success: boolean;
  status: number;
  message: string;
  access_token: string;
  refresh_token: string;
  idle_expiry: number;
  refresh_expiry: number;
  data: any;
}

class ApiService {
  private static instance: ApiService;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;
  private secureStorage: SecureStorageService;

  private constructor() {
    this.secureStorage = SecureStorageService.getInstance();
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private async getDefaultHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Get auth token - check encrypted storage first, then fallback to unencrypted
    let token: string | null = null;
    try {
      token = await this.secureStorage.getLocalItem('token') ||
              await this.secureStorage.getSessionItem('auth_token');
    } catch (error) {
      // Fallback to unencrypted
      token = localStorage.getItem('token') || sessionStorage.getItem('auth_token');
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Fallback: check session data
      let sessionData: string | null = null;
      try {
        sessionData = await this.secureStorage.getSessionItem('ohif_session');
      } catch (error) {
        sessionData = sessionStorage.getItem('ohif_session');
      }

      if (sessionData) {
        try {
          const session = JSON.parse(sessionData);
          if (session.user && session.user.id) {
            headers['Authorization'] = `Bearer ${session.user.id}`;
          }
        } catch (error) {
          console.warn('Failed to parse session data:', error);
        }
      }
    }

    return headers;
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit = {},
    retryAfterRefresh: boolean = true
  ): Promise<ApiResponse<T>> {
    try {
      const defaultHeaders = await this.getDefaultHeaders();
      const headers = {
        ...defaultHeaders,
        ...options.headers,
      };

      // Remove Authorization header if explicitly set to empty string (for login only)
      if (headers['Authorization'] === '') {
        delete headers['Authorization'];
      } else if (headers['Authorization']) {
        // Request includes Authorization token
      } else {
        console.warn('ApiService: No Authorization token available for request:', url);
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Handle token expiration (401 Unauthorized)
        // Don't attempt refresh if this is the refresh endpoint itself or login endpoint
        const isRefreshEndpoint = url.includes('/auth/refresh');
        const isLoginEndpoint = url.includes('/auth/login');

        if (response.status === 401 && retryAfterRefresh && !isRefreshEndpoint && !isLoginEndpoint) {
          console.warn('ApiService: Received 401 error, attempting token refresh...');

          // Check if we have a refresh token
          const refreshToken = sessionStorage.getItem('refresh_token');
          if (!refreshToken) {
            console.warn('ApiService: No refresh token available, logging out...');
            this.handleTokenExpiration();
            return {
              success: false,
              error: data.message || data.error || `HTTP ${response.status}: ${response.statusText}`,
            };
          }

          // Attempt to refresh the token
          const refreshSuccess = await this.refreshAccessToken();

          if (refreshSuccess) {
            // Retry the original request with new token
            return this.makeRequest<T>(url, options, false); // Don't retry again if this fails
          } else {
            // Refresh failed, logout
            console.error('ApiService: Token refresh failed, logging out...');
            this.handleTokenExpiration();
            return {
              success: false,
              error: data.message || data.error || `HTTP ${response.status}: ${response.statusText}`,
            };
          }
        } else if (response.status === 401 && (!retryAfterRefresh || isRefreshEndpoint || isLoginEndpoint)) {
          // Already tried refresh, still 401, or refresh/login endpoint returned 401
          // For login endpoint, don't logout - just return the error
          if (isLoginEndpoint) {
            // Don't call handleTokenExpiration for login failures
          } else if (isRefreshEndpoint) {
            console.error('ApiService: Refresh token endpoint returned 401, refresh token expired, logging out...');
            this.handleTokenExpiration();
          } else {
            console.error('ApiService: Request failed after token refresh, logging out...');
            this.handleTokenExpiration();
          }
        }

        return {
          success: false,
          error: data.message || data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Refreshes the access token using the refresh token
   * Returns true if refresh was successful, false otherwise
   */
  private async refreshAccessToken(): Promise<boolean> {
    // If already refreshing, wait for the existing refresh to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start refresh process
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Performs the actual refresh token API call
   */
  private async performRefresh(): Promise<boolean> {
    let refreshToken: string | null = null;
    try {
      refreshToken = await this.secureStorage.getSessionItem('refresh_token');
    } catch (error) {
      // Fallback to unencrypted
      refreshToken = sessionStorage.getItem('refresh_token');
    }

    if (!refreshToken) {
      console.error('ApiService: No refresh token available');
      return false;
    }

    try {
      const baseUrl = this.getAuthServiceUrl();
      const refreshUrl = this.buildUrl('/auth/refresh', undefined, baseUrl);

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${refreshToken}`,
        },
      });

      let data: RefreshTokenResponse;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const textData = await response.text();
        console.error('ApiService: Refresh token response is not JSON:', textData);
        return false;
      }

      if (!response.ok || !data.is_success) {
        console.error('ApiService: Refresh token failed:', data.message || 'Unknown error');
        return false;
      }

      // Update tokens securely
      if (data.access_token) {
        await this.secureStorage.setSessionItem('auth_token', data.access_token);
        await this.secureStorage.setLocalItem('token', data.access_token);
      }

      if (data.refresh_token) {
        await this.secureStorage.setSessionItem('refresh_token', data.refresh_token);
      }

      return true;
    } catch (error) {
      console.error('ApiService: Error refreshing token:', error);
      return false;
    }
  }

  private handleTokenExpiration(): void {
    void (async () => {
      try {
        const { autoSaveViewerDraftBeforeLogoutIfNeeded } = await import(
          '../utils/viewerLogoutAutoSave'
        );
        await autoSaveViewerDraftBeforeLogoutIfNeeded();
      } catch (error) {
        console.warn('[ApiService] Viewer auto-save before token expiration failed:', error);
      }

      this.clearAuthStorageAfterExpiration();
      window.dispatchEvent(new CustomEvent('token-expired'));
    })();
  }

  private clearAuthStorageAfterExpiration(): void {
    // Clear all authentication data (both encrypted and unencrypted)
    this.secureStorage.removeLocalItem('token');
    this.secureStorage.removeSessionItem('auth_token');
    this.secureStorage.removeSessionItem('refresh_token');
    this.secureStorage.removeSessionItem('ohif_session');
    this.secureStorage.removeSessionItem('idle_expiry');

    // Also clear unencrypted fallbacks
    localStorage.removeItem('token');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('ohif_session');
    sessionStorage.removeItem('idle_expiry');
  }

  public async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    baseUrl?: string
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, params, baseUrl);
    return this.makeRequest<T>(url, { method: 'GET' });
  }

  public async post<T>(
    endpoint: string,
    body?: any,
    baseUrl?: string,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, undefined, baseUrl);
    return this.makeRequest<T>(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers: options?.headers,
    });
  }

  public async put<T>(
    endpoint: string,
    body?: any,
    baseUrl?: string,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, undefined, baseUrl);
    return this.makeRequest<T>(url, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      headers: options?.headers,
    });
  }

  public async delete<T>(
    endpoint: string,
    params?: Record<string, any>,
    baseUrl?: string
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, params, baseUrl);
    return this.makeRequest<T>(url, { method: 'DELETE' });
  }

  public async postFormData<T>(
    endpoint: string,
    formData: FormData,
    baseUrl?: string
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, undefined, baseUrl);
    const headers = await this.getDefaultHeaders();
    // Remove Content-Type header to let browser set it with boundary for FormData
    delete headers['Content-Type'];

    return this.makeRequest<T>(url, {
      method: 'POST',
      body: formData,
      headers,
    });
  }

  private buildUrl(
    endpoint: string,
    params?: Record<string, any>,
    baseUrl?: string
  ): string {
    let url: string;

    if (baseUrl) {
      const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
      url = `${normalizedBase}${normalizedEndpoint}`;
    } else {
      url = endpoint;
    }

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      return queryString ? `${url}?${queryString}` : url;
    }

    return url;
  }

  // Convenience methods for different services
  public getUserManagementUrl(): string {
    return ENV.api_baseurl;
  }

  public getClientManagementUrl(): string {
    return ENV.api_baseurl;
  }

  public getAuthServiceUrl(): string {
    return ENV.api_baseurl;
  }

  public getGradingServiceUrl(): string {
    return ENV.api_baseurl;
  }

  public getProtocolServiceUrl(): string {
    return ENV.api_baseurl;
  }

  public getWorkOrderServiceUrl(): string {
    return ENV.api_baseurl;
  }

  public async getSignedUploadUrl(fileName: string, contentType: string, patientId: string = 'PATIENT_001', folderName: string = 'documents'): Promise<ApiResponse<any>> {
    const requestData = {
      file_name: fileName,
      content_type: contentType,
      url_method: 'POST',
      patient_id: patientId,
      folder_name: folderName,
      expires_in: 3600
    };

    return this.post('/file/upload-url', requestData, this.getWorkOrderServiceUrl());
  }

  public async getFileSignedUrl(fileUrl: string, expiry: number = 3600): Promise<ApiResponse<any>> {
    const requestData = {
      url: fileUrl,
      expiry,
    };

    return this.post('/file/signed-url', requestData, this.getWorkOrderServiceUrl());
  }
}

export default ApiService;
